import { useState, useEffect, useMemo, useCallback } from "react";
import { Navbar } from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Search, X, Mail, Phone, Star, ShoppingBag,
  Facebook, Instagram, Linkedin, MessageCircle, TreePine,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { filterProvinces } from "@/lib/filterData";
import { MapContainer } from "react-leaflet/MapContainer";
import { TileLayer } from "react-leaflet/TileLayer";
import { useMap } from "react-leaflet/hooks";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useSeo } from "@/hooks/useSeo";
import MarkerClusterGroupComponent from "@/components/members/MarkerClusterGroup";
import { resolveCoords, jitterCoords } from "@/lib/municipalityCoords";
import { useIsMobile } from "@/hooks/use-mobile";
import MembersMobileLayout, { type MobileMemberProfile } from "@/components/members/MembersMobileLayout";
import { defaultFilters, type MemberFilters } from "@/components/members/MemberFilterModal";

// Fix default marker icon
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

function getInitials(name: string) {
  return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
}

function getMemberCoords(member: { id: string; municipality?: string | null; location: string | null }): [number, number] | null {
  const base = resolveCoords({ municipality: member.municipality, location: member.location });
  if (!base) return null;
  // No jitter: members without a more granular address than kommun should
  // share the kommun's centroid so the cluster plugin can group them. Marker
  // clustering then visually represents how many members are in the kommun.
  return base;
}

function getProvinceForLocation(location: string | null): { province: string | null; kommun: string | null } {
  if (!location) return { province: null, kommun: null };
  for (const p of filterProvinces) {
    if (p.municipalities.includes(location)) return { province: p.name, kommun: location };
  }
  const found = filterProvinces.find((p) => p.name === location);
  if (found) return { province: found.name, kommun: null };
  return { province: null, kommun: location };
}

function extractUsername(url: string): string {
  try {
    const path = new URL(url).pathname.replace(/\/+$/, "");
    const segments = path.split("/").filter(Boolean);
    return segments[segments.length - 1] || url;
  } catch { return url; }
}

interface MemberProfile {
  id: string;
  username: string | null;
  full_name: string;
  location: string | null;
  municipality: string | null;
  province: string | null;
  avatar_url: string | null;
  phone: string | null;
  facebook_url: string | null;
  instagram_url: string | null;
  linkedin_url: string | null;
  whatsapp_number: string | null;
  skogsforum_url: string | null;
  account_type: "personal" | "company" | "admin" | null;
  email_verified?: boolean | null;
  company_name?: string | null;
}

function FlyToHandler({ target }: { target: [number, number] | null }) {
  const map = useMap();
  useEffect(() => {
    if (target) map.flyTo(target, 11, { duration: 1.5 });
  }, [target, map]);
  return null;
}


function StarRating({ rating, count }: { rating: number; count?: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className={`h-4 w-4 ${i <= Math.round(rating) ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground/30"}`} />
      ))}
    </div>
  );
}

function SocialBadge({ icon: Icon, label, url }: { icon: any; label: string; url: string }) {
  return (
    <a href={url} target="_blank" rel="noopener noreferrer"
      className="inline-flex items-center gap-1.5 rounded-md border border-border bg-secondary px-2.5 py-1 text-xs font-medium text-foreground transition-colors hover:bg-accent">
      <Icon className="h-3.5 w-3.5" />
      <span>{label}</span>
    </a>
  );
}

function MemberDetailPanel({ member, onClose }: { member: MemberProfile; onClose: () => void }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const loc = getProvinceForLocation(member.location);
  const locationStr = loc.province && loc.kommun ? `${loc.kommun}, ${loc.province}` : loc.kommun || loc.province || member.location;

  // Fetch email from auth — we'll use user_id to get it via a query approach
  // Since we can't access auth.users, we show email only if logged in and use a placeholder
  const isLoggedIn = !!user;

  const { data: reviews } = useQuery({
    queryKey: ["member-reviews", member.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("purchase_reviews")
        .select("rating")
        .eq("target_user_id", member.id);
      return data || [];
    },
  });

  const { data: listingsCount } = useQuery({
    queryKey: ["member-listings-count", member.id],
    queryFn: async () => {
      const { count } = await supabase
        .from("listings")
        .select("*", { count: "exact", head: true })
        .eq("seller_id", member.id)
        .eq("status", "aktiv");
      return count ?? 0;
    },
  });

  const reviewCount = reviews?.length ?? 0;
  const avgRating = reviewCount > 0 ? reviews!.reduce((s, r) => s + r.rating, 0) / reviewCount : 0;

  const hasSocials = member.facebook_url || member.instagram_url || member.linkedin_url || member.whatsapp_number || member.skogsforum_url;

  return (
    <div className="flex h-full flex-col rounded-xl border border-border bg-card/95 shadow-xl backdrop-blur-sm">
      {/* Header with close button */}
      <div className="flex items-center justify-between border-b border-border p-4">
        <span className="text-sm font-semibold text-foreground">Medlemsdetaljer</span>
        <button onClick={onClose} className="rounded-md p-1 text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors">
          <X className="h-4 w-4" />
        </button>
      </div>

      <ScrollArea className="flex-1">
        <div className="space-y-5 p-4">
          {/* Profile container */}
          <div className="flex items-center gap-3">
            <Avatar className="h-14 w-14">
              {member.avatar_url && <AvatarImage src={member.avatar_url} alt={member.full_name} />}
              <AvatarFallback className="text-sm">{getInitials(member.full_name)}</AvatarFallback>
            </Avatar>
            <div>
              <p className="text-base font-bold text-foreground">{member.full_name}</p>
              {locationStr && <p className="text-sm text-muted-foreground">{locationStr}</p>}
            </div>
          </div>

          {/* Contact methods */}
          <div className="space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Kontakt</h3>
            {isLoggedIn ? (
              <div className="space-y-2">
                {member.phone && (
                  <div className="flex items-center gap-2 text-sm text-foreground">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span>{member.phone}</span>
                  </div>
                )}
                {hasSocials && (
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {member.facebook_url && <SocialBadge icon={Facebook} label={extractUsername(member.facebook_url)} url={member.facebook_url} />}
                    {member.instagram_url && <SocialBadge icon={Instagram} label={extractUsername(member.instagram_url)} url={member.instagram_url} />}
                    {member.linkedin_url && <SocialBadge icon={Linkedin} label={extractUsername(member.linkedin_url)} url={member.linkedin_url} />}
                    {member.whatsapp_number && <SocialBadge icon={MessageCircle} label={member.whatsapp_number} url={`https://wa.me/${member.whatsapp_number.replace(/[^0-9]/g, "")}`} />}
                    {member.skogsforum_url && <SocialBadge icon={TreePine} label="Skogsforum" url={member.skogsforum_url} />}
                  </div>
                )}
                {!member.phone && !hasSocials && (
                  <p className="text-xs text-muted-foreground">Inga kontaktuppgifter angivna.</p>
                )}
              </div>
            ) : (
              <button onClick={() => navigate("/logga-in")} className="text-sm text-primary hover:underline">
                Logga in för att se kontaktuppgifter
              </button>
            )}
          </div>

          {/* Reviews */}
          <div className="space-y-1.5">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Recensioner</h3>
            {reviewCount > 0 ? (
              <div>
                <StarRating rating={avgRating} />
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {avgRating.toFixed(1)} / 5 ({reviewCount} {reviewCount === 1 ? "recension" : "recensioner"})
                </p>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">Inga recensioner ännu.</p>
            )}
          </div>

          {/* Active listings */}
          <div className="space-y-1.5">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Annonser</h3>
            <button
              onClick={() => navigate(`/saljare/${member.username || member.id}`)}
              className="flex items-center gap-2 text-sm text-foreground hover:text-primary transition-colors"
            >
              <ShoppingBag className="h-4 w-4 text-muted-foreground" />
              <span>{listingsCount ?? 0} aktiva annonser</span>
            </button>
          </div>

          {/* View full profile */}
          <Button className="w-full" onClick={() => navigate(`/saljare/${member.username || member.id}`)}>
            Visa fullständig profil
          </Button>
        </div>
      </ScrollArea>
    </div>
  );
}

export default function Members() {
  useSeo({
    title: "Säljare — skogsägare och leverantörer på TimberNet",
    description: "Utforska kartan över skogsägare, leverantörer och företag på TimberNet. Hitta säljare av virke och tjänster nära dig.",
    path: "/saljare",
  });
  const [search, setSearch] = useState("");
  const [flyTarget, setFlyTarget] = useState<[number, number] | null>(null);
  const [selectedMember, setSelectedMember] = useState<MemberProfile | null>(null);
  const [filters, setFilters] = useState<MemberFilters>(defaultFilters);
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const { user } = useAuth();

  const { data: members = [], isLoading } = useQuery({
    queryKey: ["all-members", !!user],
    queryFn: async () => {
      // Admin filtering: when signed in we explicitly fetch admin IDs and exclude
      // them; for anonymous visitors the public_profiles view already excludes
      // admin accounts server-side, so we skip the RPC (it's restricted to authed).
      let adminIds: string[] = [];
      if (user) {
        const { data: adminRows } = await (supabase as any).rpc("list_admin_user_ids");
        adminIds = (adminRows || []).map((r: any) =>
          typeof r === "string" ? r : r.list_admin_user_ids ?? r.user_id ?? r
        );
      }

      // Always read from the public_profiles view: the base profiles table is now
      // restricted to row owners + admins, so querying it would return only the
      // current user's own row when signed in.
      const cols = "id, username, full_name, location, municipality, province, avatar_url, facebook_url, instagram_url, linkedin_url, skogsforum_url, account_type, company_name";
      let q = (supabase as any).from("public_profiles").select(cols).neq("full_name", "");
      if (adminIds.length > 0) {
        q = q.not("id", "in", `(${adminIds.join(",")})`);
      }
      const { data, error } = await q;
      if (error) throw error;
      return ((data || []) as any[]).map((m) => ({
        phone: null,
        whatsapp_number: null,
        email_verified: null,
        ...m,
      })) as MemberProfile[];
    },
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return members.filter((m) => {
      if (q) {
        const matches =
          m.full_name.toLowerCase().includes(q) ||
          (m.location && m.location.toLowerCase().includes(q)) ||
          (m.municipality && m.municipality.toLowerCase().includes(q)) ||
          (m.company_name && m.company_name.toLowerCase().includes(q));
        if (!matches) return false;
      }
      if (filters.accountType !== "all" && m.account_type !== filters.accountType) return false;
      if (filters.verified === "verified" && !m.email_verified) return false;
      if (filters.province && m.province !== filters.province) return false;
      if (filters.municipality && m.municipality !== filters.municipality) return false;
      return true;
    });
  }, [members, search, filters]);

  const clusterMarkers = useMemo(
    () => filtered
      .map((m) => ({ id: m.id, coords: getMemberCoords(m) }))
      .filter((m): m is { id: string; coords: [number, number] } => m.coords !== null),
    [filtered]
  );

  const handleMemberClick = (member: MemberProfile) => {
    setSelectedMember(member);
    const coords = getMemberCoords(member);
    if (coords) setFlyTarget(coords);
  };

  const handleMarkerClick = useCallback((id: string) => {
    const member = members.find((m) => m.id === id);
    if (member) handleMemberClick(member);
  }, [members]);

  // ---------- Mobile layout ----------
  if (isMobile) {
    return (
      <div className="flex h-screen flex-col overflow-hidden">
        <Navbar />
        <div className="relative flex-1 overflow-hidden" style={{ isolation: "isolate" }}>
          <div className="absolute inset-0 z-0">
            <MapContainer
              center={[62.0, 15.0]}
              zoom={5}
              style={{ height: "100%", width: "100%" }}
              zoomControl={false}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <FlyToHandler target={flyTarget} />
              <MarkerClusterGroupComponent
                markers={clusterMarkers}
                onMarkerClick={handleMarkerClick}
                selectedId={selectedMember?.id ?? null}
              />
            </MapContainer>
          </div>
          <MembersMobileLayout
            members={filtered as MobileMemberProfile[]}
            isLoading={isLoading}
            selectedMember={(selectedMember as MobileMemberProfile) ?? null}
            onSelectMember={(m) => setSelectedMember(m as MemberProfile | null)}
            search={search}
            onSearchChange={setSearch}
            filters={filters}
            onFiltersChange={setFilters}
          />
        </div>
      </div>
    );
  }

  // ---------- Desktop layout (unchanged) ----------
  return (
    <div className="flex min-h-screen flex-col">
      <Navbar />
      <div className="relative flex-1" style={{ isolation: "isolate" }}>
        {/* Full-screen map */}
        <div className="absolute inset-0 z-0">
          <MapContainer
            center={[62.0, 15.0]}
            zoom={5}
            style={{ height: "100%", width: "100%" }}
            zoomControl={false}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <FlyToHandler target={flyTarget} />
            <MarkerClusterGroupComponent markers={clusterMarkers} onMarkerClick={handleMarkerClick} selectedId={selectedMember?.id ?? null} />
          </MapContainer>
        </div>

        {/* Floating panels */}
        <div className="absolute left-0 top-0 z-10 flex h-full gap-2 p-4">
          {/* Members list panel */}
          <div className={`flex h-full flex-col rounded-xl border border-border bg-card/95 shadow-xl backdrop-blur-sm transition-all duration-300 ${selectedMember ? "w-72" : "w-96"}`}>
            <div className="border-b border-border p-4">
              <h1 className="mb-3 text-lg font-bold text-foreground">Medlemmar</h1>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input placeholder="Sök medlem..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
              </div>
            </div>

            <ScrollArea className="flex-1">
              <div className="p-2">
                {isLoading ? (
                  <p className="p-4 text-center text-sm text-muted-foreground">Laddar medlemmar...</p>
                ) : filtered.length === 0 ? (
                  <p className="p-4 text-center text-sm text-muted-foreground">Inga medlemmar hittades.</p>
                ) : (
                  filtered.map((member) => (
                    <div
                      key={member.id}
                      className={`flex cursor-pointer items-center gap-3 rounded-lg p-3 transition-colors hover:bg-secondary ${selectedMember?.id === member.id ? "bg-secondary" : ""}`}
                      onClick={() => handleMemberClick(member)}
                    >
                      <Avatar className="h-10 w-10 shrink-0">
                        {member.avatar_url && <AvatarImage src={member.avatar_url} alt={member.full_name} />}
                        <AvatarFallback className="text-xs">{getInitials(member.full_name)}</AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-foreground">{member.full_name}</p>
                        {member.location && <p className="truncate text-xs text-muted-foreground">{member.location}</p>}
                      </div>
                      {!selectedMember && (
                        <Button size="sm" className="shrink-0" onClick={(e) => { e.stopPropagation(); navigate(`/saljare/${member.username || member.id}`); }}>
                          Visa profil
                        </Button>
                      )}
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>

            <div className="border-t border-border p-3 text-center text-xs text-muted-foreground">
              {filtered.length} medlem{filtered.length !== 1 ? "mar" : ""}
            </div>
          </div>

          {/* Detail panel — slides in when a member is selected */}
          {selectedMember && (
            <div className="h-full w-80 animate-in slide-in-from-left-4 duration-300">
              <MemberDetailPanel member={selectedMember} onClose={() => setSelectedMember(null)} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

